import React, { useContext, useState } from 'react';
import { Box, Switch } from '@warp-ds/react';
import { SchibstedPrivacySettingsContext } from '../schibsted-privacy-settings-context.js';
import { Scope } from '../../scope.js';

function isConsentSetting(
    setting: ConsentSetting | ObjectionSetting,
): setting is ConsentSetting {
    return (setting as ConsentSetting).currentState.hasConsent !== undefined;
}

export default function Category(props: {
    category: ObjectionCategory | ConsentCategory;
    scope: Scope;
}): React.ReactElement {
    return (
        <Box bleed className="s-bg-default mt-16 mb-16">
            {props.category.settings.length > 1 ? (
                <h2 className="text-l mb-16">{props.category.categoryTitle}</h2>
            ) : (
                <></>
            )}
            <div>
                {props.category.settings.map((setting, index) =>
                    isConsentSetting(setting) ? (
                        <ConsentToggle
                            key={setting.key.id}
                            setting={setting}
                            isLast={
                                index === props.category.settings.length - 1
                            }
                            scope={props.scope}
                        />
                    ) : (
                        <ObjectionToggle
                            key={setting.key.id}
                            setting={setting}
                            isLast={
                                index === props.category.settings.length - 1
                            }
                            scope={props.scope}
                        />
                    ),
                )}
            </div>
        </Box>
    );
}

export function ObjectionToggle(props: {
    setting: ObjectionSetting;
    isLast: boolean;
    scope: Scope;
}): React.ReactElement {
    const ctx = useContext(SchibstedPrivacySettingsContext);
    const [updating, setUpdating] = useState(false);

    const changeSetting = () => {
        if (!updating) {
            setUpdating(true);
            if (props.setting.currentState.hasObjection) {
                if (props.setting.currentState.objectionKey) {
                    ctx.withdrawObjection(props.setting, props.scope).finally(
                        () => setUpdating(false),
                    );
                }
            } else {
                ctx.addObjection(props.setting, props.scope).finally(() =>
                    setUpdating(false),
                );
            }
        }
    };

    return (
        <>
            <div className="pb-8">
                <h3 className="text-m">{props.setting.content.title}</h3>
                <div className="flex">
                    <div className="grow text-s pr-16">
                        <p>{props.setting.content.description}</p>
                    </div>
                    <Switch
                        value={!props.setting.currentState.hasObjection}
                        onClick={changeSetting}
                        aria-label={props.setting.content.title}
                    />
                </div>
            </div>
            {props.isLast ? '' : <hr className="mb-16" />}
        </>
    );
}

export function ConsentToggle(props: {
    setting: ConsentSetting;
    isLast: boolean;
    scope: Scope;
}): React.ReactElement {
    const ctx = useContext(SchibstedPrivacySettingsContext);
    const [updating, setUpdating] = useState(false);

    const changeSetting = () => {
        if (!updating) {
            setUpdating(true);
            if (props.setting.currentState.hasConsent) {
                if (props.setting.currentState.consentKey) {
                    ctx.withdrawConsent(props.setting, props.scope).finally(
                        () => setUpdating(false),
                    );
                }
            } else {
                ctx.addConsent(props.setting, props.scope).finally(() =>
                    setUpdating(false),
                );
            }
        }
    };

    return (
        <>
            <div className="pb-8">
                <h3 className="text-m">{props.setting.content.title}</h3>
                <div className="flex">
                    <div className="grow text-s pr-16">
                        <p>{props.setting.content.description}</p>
                    </div>
                    <Switch
                        value={props.setting.currentState.hasConsent}
                        onClick={changeSetting}
                        aria-label={props.setting.content.title}
                    />
                </div>
            </div>
            {props.isLast ? '' : <hr className="mb-16" />}
        </>
    );
}
